import { Outlet, createBrowserRouter, type RouteObject } from 'react-router';
import { RequireAuth, RequirePermission } from '../auth/guards';
import { AdminIndex } from './AdminIndex';
import { ADMIN_PAGES, type AdminPage } from './adminPages';
import { AppLayout } from './AppLayout';
import { LoadingScreen } from './LoadingScreen';
import { LoginPage } from './LoginPage';
import { NotFoundPage } from './NotFoundPage';
import { PageError } from './PageError';

/** 各ページの Task で、ここに自分のルートを追加する(lazy でページ単位に分割)。 */
export const pageRoutes: RouteObject[] = [
  {
    index: true,
    lazy: async () => ({
      Component: (await import('../features/dashboard/DashboardPage')).DashboardPage,
    }),
  },
  {
    path: 'cases',
    lazy: async () => ({
      Component: (await import('../features/cases/CasesPage')).CasesPage,
    }),
  },
  {
    path: 'calendar',
    lazy: async () => ({
      Component: (await import('../features/calendar/CalendarPage')).CalendarPage,
    }),
  },
  {
    path: 'search',
    lazy: async () => ({
      Component: (await import('../features/search/SearchPage')).SearchPage,
    }),
  },
  {
    path: 'master',
    lazy: async () => ({
      Component: (await import('../features/master/MasterPage')).MasterPage,
    }),
  },
  {
    path: 'simulation',
    lazy: async () => ({
      Component: (await import('../features/simulation/SimulationPage')).SimulationPage,
    }),
  },
  {
    path: 'schedule',
    lazy: async () => ({
      Component: (await import('../features/schedule/SchedulePage')).SchedulePage,
    }),
  },
];

/**
 * /admin/<path>: 権限のある人だけ(RequirePermission で画面を出し分ける)。ページ本体は lazy で、初回に読み込むコードを減らすため。
 * lazy のコードは、ルートが一致した時点でガードの判定とは関係なく読み込まれる(権限のない人にも届く)。
 * 守れているのは画面の表示だけで、データと書き込みを守るのはサーバーの権限チェック。
 */
const adminRoute = (page: AdminPage): RouteObject => ({
  path: page.path,
  ErrorBoundary: PageError,
  element: (
    <RequirePermission permission={page.permission}>
      <Outlet />
    </RequirePermission>
  ),
  children: [
    {
      index: true,
      lazy: async () => ({ Component: (await page.load()).Component }),
    },
  ],
});

export const buildRoutes = (
  pages: RouteObject[] = pageRoutes,
  adminPages: readonly AdminPage[] = ADMIN_PAGES,
): RouteObject[] => [
  { path: '/login', Component: LoginPage, ErrorBoundary: PageError },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout adminPages={adminPages} />
      </RequireAuth>
    ),
    HydrateFallback: LoadingScreen,
    children: [
      // 各ページ個別の ErrorBoundary: ページが落ちてもレイアウト(サイドバー)は残る
      ...pages.map((page) => ({ ...page, ErrorBoundary: PageError })),
      {
        path: 'admin',
        children: [
          { index: true, element: <AdminIndex pages={adminPages} /> },
          ...adminPages.map(adminRoute),
        ],
      },
      { path: '*', Component: NotFoundPage },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(buildRoutes());
